import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import PropTypes from 'prop-types';

import { updateCustomer } from 'api/customers';
import { getAllPolygraphies, updatePolygraphy } from 'api/polygraphies';
import { getAllTechnicians } from 'api/technicians';
import { getAllDoctors } from 'api/doctors';
import { filtersToQueryString, initialFilters } from 'helpers/filtering';
import { setDoctorOptions } from 'helpers/doctor';
import { setTechnicianOptions } from 'helpers/technician';
import { formatDatetime } from 'helpers/timezone';
import { sanitizePolygraphy } from 'helpers/general';

import MainViewWithTable from 'components/main_view_with_table';

import { COLUMNS, CSV_HEADERS } from 'config/polygraphy';

const Polygraphies = ({ onCountUpdate, onMessages }) => {
  const history = useHistory();
  const [meta, setMeta] = useState({});
  const [columns, setColumns] = useState(COLUMNS);
  const [fetching, setFetching] = useState(true);
  const [allPolygraphies, setAllPolygraphies] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [allDoctors, setDoctors] = useState([]);
  const [allTechnicians, setTechnicians] = useState([]);

  const fetchData = async (page, filters = {}, search) => {
    const filtersString = filtersToQueryString(filters, search);
    const data = await getAllPolygraphies(page, filtersString);

    setAllPolygraphies([...data.items]);
    setMeta(data.meta);
    setFetching(false);
  };

  useEffect(() => {
    (async () => {
      const allTechnicians = await getAllTechnicians();
      const allDoctors = await getAllDoctors();
      const allPolys = await getAllPolygraphies(
        1,
        '&status=to-be-scheduled',
        0,
      );
      setDoctors(allDoctors);
      setTechnicians(allTechnicians);

      setExportData(
        allPolys.items
          .filter((i) =>
            ['not-required', 'paid'].includes(i.manualPaymentStatus),
          )
          .map((i) => ({
            country: 'France',
            city: i.customer?.city,
            address: i.customer?.address,
            name: [i.customer?.firstName, i.customer?.lastName]
              .filter(Boolean)
              .join(' '),
            phone: i.customer?.phone,
            technicianFullName: [
              i.technician?.firstName,
              i.technician?.lastName,
            ]
              .filter(Boolean)
              .join(' '),
            manualPaymentStatus: i.manualPaymentStatus,
            pgNotes: i.notes,
            additionalNotes: i.customer?.notes,
            createdAt: i.createdAt,
            link: `${process.env.REACT_APP_URL}/polygraphies/${i.id}`,
          })),
      );

      const newColumnsWithDocs = setDoctorOptions({
        columns: COLUMNS,
        data: allDoctors,
      });

      const newColumnsWithTechs = setTechnicianOptions({
        columns: newColumnsWithDocs,
        data: allTechnicians,
      });

      const initFilters = initialFilters(COLUMNS);
      await fetchData(1, initFilters);

      setFetching(false);
      setColumns(newColumnsWithTechs);
    })();
  }, []);

  const handleSelect = async (prop, value, p) => {
    const index = allPolygraphies.findIndex((t) => t.id === p.id);

    if (prop === 'doctor') {
      value = allDoctors.find(({ id }) => Number(id) === Number(value)) || null;
    }

    if (prop === 'technician') {
      value =
        allTechnicians.find(({ id }) => Number(id) === Number(value)) || null;
    }

    const sanitizedPolygraphy = sanitizePolygraphy({ ...p });

    const {
      polygraphy,
      meta: { messages },
    } = await updatePolygraphy(p.id, {
      ...sanitizedPolygraphy,
      [prop]: value,
    });

    const newPolygraphies = [...allPolygraphies];
    newPolygraphies[index] = polygraphy;

    setAllPolygraphies([...newPolygraphies]);
    onCountUpdate();
    onMessages(messages);
  };

  const handleDateUpdate = async (pickupDate, deliveryDate, p) => {
    const index = allPolygraphies.findIndex((t) => t.id === p.id);

    const status =
      allPolygraphies[index].status === 'blank'
        ? 'scheduled'
        : allPolygraphies[index].status;

    const pickupDateStr = pickupDate?.toISOString() || null;
    const deliveryDateStr = deliveryDate?.toISOString() || null;

    const sanitizedPolygraphy = sanitizePolygraphy({ ...p });

    const {
      polygraphy,
      meta: { messages },
    } = await updatePolygraphy(p.id, {
      ...sanitizedPolygraphy,
      pickupDate: pickupDateStr,
      deliveryDate: deliveryDateStr,
      status,
    });

    const newPolygraphies = [...allPolygraphies];
    newPolygraphies[index] = polygraphy;

    setAllPolygraphies([...newPolygraphies]);
    onCountUpdate();
    onMessages(messages);
  };

  const handleTextUpdate = async (value, p, col) => {
    const index = allPolygraphies.findIndex((t) => t.id === p.id);
    const newPolygraphies = [...allPolygraphies];

    if (col.accessor === 'notes') {
      const {
        polygraphy,
        meta: { messages },
      } = await updatePolygraphy(p.id, {
        notes: value,
      });

      onMessages(messages);
      newPolygraphies[index] = polygraphy;
    } else {
      await updateCustomer(p.customer.id, {
        [col.accessor]: value,
      });

      newPolygraphies[index].customer[col.accessor] = value;
    }

    setAllPolygraphies([...newPolygraphies]);

    onCountUpdate();
  };

  const props = {
    data: allPolygraphies,
    columns,
    fetchData,
    fetching,
    meta,
    onSelect: handleSelect,
    onTextUpdate: handleTextUpdate,
    onDatePick: handleDateUpdate,
    title: 'Polygraphies',
    accessor: 'polygraphies',
  };

  return (
    <MainViewWithTable {...props}>
      <button
        className="btn-white mr5"
        data-cy="calendar"
        onClick={() => history.push('/calendar/exams')}
      >
        Calendar view
      </button>
      <CSVLink
        data={exportData}
        headers={CSV_HEADERS}
        filename={`polygraphies_for_myl_${formatDatetime(new Date())}.csv`}
      >
        <button className="btn-white mr5" data-cy="download">
          Export for MapYourList
        </button>
      </CSVLink>
      <button
        className="btn-green"
        data-cy="submit"
        onClick={() => history.push('/polygraphies/add')}
      >
        Add polygraphy
      </button>
    </MainViewWithTable>
  );
};

Polygraphies.propTypes = {
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default Polygraphies;
